import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, CircleAlert, Info, X, XCircle } from 'lucide-react';
import type { BannerKind, BannerStatus, CheckStatus } from '../../shared/types';
import { KIND_LABELS } from '../lib/format';

/**
 * Logo in einer festen Kachel: beliebige Seitenverhältnisse und SVGs ohne feste Größe
 * werden eingepasst statt überzulaufen.
 */
export function LogoTile({
  src,
  alt,
  width,
  height,
  align = 'left',
  plate = false,
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  align?: 'left' | 'center';
  plate?: boolean;
}) {
  return (
    <span className={`logo-tile ${plate ? 'has-plate' : ''}`} style={{ width, height }}>
      <img src={src} alt={alt} style={{ objectPosition: align === 'center' ? 'center' : 'left center' }} />
    </span>
  );
}

/** Text mit *Hervorhebung* in der Akzentfarbe (für frei einstellbare Überschriften). */
export function Emphasis({ text }: { text: string }) {
  return (
    <>
      {text
        .split(/(\*[^*]+\*)/g)
        .map((part, i) =>
          part.startsWith('*') && part.endsWith('*') && part.length > 2 ? (
            <em key={i}>{part.slice(1, -1)}</em>
          ) : (
            part
          ),
        )}
    </>
  );
}

export function Spinner({ label = 'Lädt …' }: { label?: string }) {
  return (
    <div className="center-fill" aria-busy="true">
      <div className="spinner" role="progressbar" aria-label={label} />
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {children(id)}
      {hint && !error && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

const STATUS_LABEL: Record<BannerStatus, string> = {
  approved: 'Freigegeben',
  changes_requested: 'Änderungen',
  pending: 'Offen',
};

export function StatusChip({ status }: { status: BannerStatus }) {
  return (
    <span className={`chip chip-${status}`}>
      {status === 'approved' && <Check strokeWidth={3} />}
      {STATUS_LABEL[status]}
    </span>
  );
}

export function KindChip({ kind }: { kind: BannerKind }) {
  return <span className="chip chip-outline">{KIND_LABELS[kind]}</span>;
}

export function CheckIcon({ status, size = 16 }: { status: CheckStatus; size?: number }) {
  if (status === 'pass')
    return <Check size={size} strokeWidth={3} className="status-pass" aria-label="erfüllt" />;
  if (status === 'warn') return <AlertTriangle size={size} className="status-warn" aria-label="Hinweis" />;
  if (status === 'fail') return <XCircle size={size} className="status-fail" aria-label="nicht erfüllt" />;
  return <Info size={size} className="status-info" aria-label="Info" />;
}

export function CheckSummary({ summary }: { summary: { pass: number; warn: number; fail: number } }) {
  if (summary.fail) {
    return (
      <span className="status-icon status-fail" title="Technik-Check: Fehler">
        <XCircle size={14} /> {summary.fail}
      </span>
    );
  }
  if (summary.warn) {
    return (
      <span className="status-icon status-warn" title="Technik-Check: Hinweise">
        <AlertTriangle size={14} /> {summary.warn}
      </span>
    );
  }
  return (
    <span className="status-icon status-pass" title="Technik-Check bestanden">
      <Check size={14} strokeWidth={3} /> Check
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  children,
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon}
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function ProgressBar({
  total,
  approved,
  changes,
}: {
  total: number;
  approved: number;
  changes: number;
}) {
  const pct = (n: number) => (total ? `${(n / total) * 100}%` : '0%');
  return (
    <div className="progress" role="img" aria-label={`${approved} von ${total} freigegeben`}>
      <span className="p-approved" style={{ width: pct(approved) }} />
      <span className="p-changes" style={{ width: pct(changes) }} />
    </div>
  );
}

export function Notice({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'warn' | 'error' | 'ok';
  children: ReactNode;
}) {
  const Icon =
    kind === 'error' ? CircleAlert : kind === 'warn' ? AlertTriangle : kind === 'ok' ? Check : Info;
  return (
    <div className={`notice ${kind === 'info' ? '' : kind}`}>
      <Icon size={18} />
      <div>{children}</div>
    </div>
  );
}

/** Modaler Dialog auf Basis von <dialog> (Fokusfalle und Esc kommen vom Browser). */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      // showModal() fokussiert das erste fokussierbare Element (den Schließen-Knopf);
      // sinnvoller ist das erste Eingabefeld.
      el.querySelector<HTMLElement>(
        '.dialog-body input:not([type=hidden]):not([disabled]):not([readonly]), .dialog-body textarea, .dialog-body select',
      )?.focus();
    }
    if (!open && el.open) el.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`dialog ${wide ? 'wide' : ''}`}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onMouseDown={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {open && (
        <>
          <div className="dialog-head">
            <h2>{title}</h2>
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-icon"
              onClick={onClose}
              aria-label="Schließen"
            >
              <X size={18} />
            </button>
          </div>
          <div className="dialog-body">{children}</div>
          {footer && <div className="dialog-foot">{footer}</div>}
        </>
      )}
    </dialog>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Löschen',
  danger = true,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
                onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p>{message}</p>
    </Dialog>
  );
}

/** Kleines Dropdown-Menü; schließt bei Klick außerhalb oder Esc. */
export function Menu({
  trigger,
  children,
  align = 'right',
}: {
  trigger: (props: { onClick: () => void; 'aria-expanded': boolean }) => ReactElement;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  // Das Menü hängt am <body> und wird am Viewport ausgerichtet – so schneiden Tabellen mit
  // overflow es nicht ab. Reicht der Platz nach unten nicht, klappt es nach oben auf.
  useLayoutEffect(() => {
    if (!open || !anchor.current || !menu.current) return;
    const a = anchor.current.getBoundingClientRect();
    const m = menu.current.getBoundingClientRect();
    const below = a.bottom + 6;
    const above = a.top - 6 - m.height;
    const top = below + m.height <= window.innerHeight - 8 || above < 8 ? below : above;
    const left = align === 'right' ? a.right - m.width : a.left;
    setPlace({ top, left: Math.min(Math.max(8, left), window.innerWidth - m.width - 8) });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const inside = (target: EventTarget | null) =>
      target instanceof Node && Boolean(anchor.current?.contains(target) || menu.current?.contains(target));
    const onDown = (e: MouseEvent) => !inside(e.target) && close();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onScroll = (e: Event) => !inside(e.target) && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      setPlace(null);
    };
  }, [open]);

  return (
    <div ref={anchor} style={{ position: 'relative' }}>
      {trigger({ onClick: () => setOpen((o) => !o), 'aria-expanded': open })}
      {open &&
        createPortal(
          <div
            ref={menu}
            className="menu"
            role="menu"
            style={place ?? { top: 0, left: 0, visibility: 'hidden' }}
          >
            {children(() => setOpen(false))}
          </div>,
          document.body,
        )}
    </div>
  );
}

export function Tabs<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (value: T) => void;
  items: { value: T; label: ReactNode }[];
}) {
  return (
    <div className="tabs" role="tablist">
      {items.map((item) => (
        <button
          key={item.value}
          role="tab"
          type="button"
          aria-selected={item.value === value}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}
